import { FormattedMessage } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';

/**
 * The unsaved-changes confirmation raised by useUnsavedCloseGuard: save the
 * edits, discard them, or keep editing (also the Esc/backdrop action).
 */
export default function UnsavedChangesDialog({
    open,
    onKeepEditing,
    onDiscard,
    onSave
}: {
    open: boolean;
    onKeepEditing: () => void;
    onDiscard: () => void;
    onSave: () => void;
}) {
    return (
        <Dialog open={open} onClose={onKeepEditing} maxWidth="sm" fullWidth>
            <DialogTitle>
                <FormattedMessage id="unsavedChanges.title" />
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <FormattedMessage id="unsavedChanges.message" />
                </DialogContentText>
            </DialogContent>
            {/* Three labelled buttons don't fit a phone side by side — they
                squeeze until the labels wrap. So they stack full-width below
                `sm`, reordered so the primary action is on top and the
                destructive one sits last, away from it. */}
            <DialogActions
                sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 1,
                    px: { xs: 2, sm: 1 },
                    py: { xs: 2, sm: 1 },
                    // DialogActions spaces its children with a left margin,
                    // which is a gap in the wrong axis once stacked.
                    '& > :not(style) ~ :not(style)': { ml: 0 }
                }}
            >
                <Button
                    onClick={onKeepEditing}
                    variant="outlined"
                    color="inherit"
                    startIcon={<EditOutlinedIcon />}
                    sx={{ order: { xs: 2, sm: 0 } }}
                >
                    <FormattedMessage id="unsavedChanges.keepEditing" />
                </Button>
                <Button
                    onClick={onDiscard}
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    sx={{ order: { xs: 3, sm: 0 } }}
                >
                    <FormattedMessage id="unsavedChanges.discard" />
                </Button>
                <Button
                    onClick={onSave}
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    sx={{ order: { xs: 1, sm: 0 } }}
                >
                    <FormattedMessage id="common.save" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
